"use client";

import { createContext, useContext, useId } from "react";
import { cn } from "@/lib/utils";

/* ── Context ────────────────────────────────────────────────── */

interface TabsContextValue {
  activeTab: string;
  onTabChange: (id: string) => void;
  baseId: string;
}

const TabsContext = createContext<TabsContextValue>({
  activeTab: "",
  onTabChange: () => {},
  baseId: "",
});

/* ── Root ───────────────────────────────────────────────────── */

interface TabsProps {
  activeTab: string;
  onTabChange: (id: string) => void;
  children: React.ReactNode;
  className?: string;
}

function Tabs({ activeTab, onTabChange, children, className }: TabsProps) {
  const baseId = useId();
  return (
    <TabsContext.Provider value={{ activeTab, onTabChange, baseId }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

/* ── Tab list ───────────────────────────────────────────────── */

interface TabListProps extends React.ComponentProps<"div"> {
  /** Visual style */
  variant?: "default" | "pill";
}

function TabList({
  className,
  variant = "default",
  ...props
}: TabListProps) {
  return (
    <div
      role="tablist"
      className={cn(
        "flex",
        variant === "default" && [
          "border-b border-[var(--color-border-default)]",
          "gap-[var(--space-1)]",
        ],
        variant === "pill" && [
          "gap-[var(--space-1)]",
          "rounded-[var(--radius-full)] border border-[var(--color-border-default)]",
          "bg-[var(--color-bg-card)] p-[var(--space-1)]",
        ],
        className
      )}
      {...props}
    />
  );
}

/* ── Tab trigger ────────────────────────────────────────────── */

interface TabTriggerProps extends React.ComponentProps<"button"> {
  value: string;
  variant?: "default" | "pill";
}

function TabTrigger({
  value,
  variant = "default",
  className,
  children,
  ...props
}: TabTriggerProps) {
  const { activeTab, onTabChange, baseId } = useContext(TabsContext);
  const isActive = activeTab === value;

  return (
    <button
      role="tab"
      id={`${baseId}-tab-${value}`}
      aria-selected={isActive}
      aria-controls={`${baseId}-panel-${value}`}
      tabIndex={isActive ? 0 : -1}
      onClick={() => onTabChange(value)}
      className={cn(
        "inline-flex items-center justify-center whitespace-nowrap",
        "text-[length:var(--font-size-sm)] font-[number:var(--font-weight-medium)]",
        "transition-colors duration-[var(--duration-normal)]",
        "outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-primary-muted)]",
        "disabled:pointer-events-none disabled:opacity-50",
        variant === "default" && [
          "px-[var(--space-3)] py-[var(--space-2)] -mb-px",
          "border-b-2",
          isActive
            ? "border-[var(--color-primary)] text-[var(--color-text-primary)]"
            : "border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]",
        ],
        variant === "pill" && [
          "flex-1 px-[var(--space-4)] py-[var(--space-2)]",
          "rounded-[var(--radius-full)]",
          isActive
            ? "bg-[var(--color-primary)] text-[var(--color-primary-foreground)]"
            : "text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]",
        ],
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

/* ── Tab panel ──────────────────────────────────────────────── */

interface TabPanelProps extends React.ComponentProps<"div"> {
  value: string;
}

function TabPanel({
  value,
  className,
  children,
  ...props
}: TabPanelProps) {
  const { activeTab, baseId } = useContext(TabsContext);

  if (activeTab !== value) return null;

  return (
    <div
      role="tabpanel"
      id={`${baseId}-panel-${value}`}
      aria-labelledby={`${baseId}-tab-${value}`}
      tabIndex={0}
      className={cn("mt-[var(--space-4)] outline-none", className)}
      {...props}
    >
      {children}
    </div>
  );
}

export { Tabs, TabList, TabTrigger, TabPanel };
export type { TabsProps, TabListProps, TabTriggerProps, TabPanelProps };
